import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createDemoAudioBlob, getDemoTrackUrl } from '@/utils/createDemoAudio';

export type FocusTrack = {
  id: string;
  name: string;
  description?: string;
  category: string;
  duration?: string;
  mood?: string;
  audioType: 'calm' | 'nature' | 'focus' | 'ambient';
};

interface FocusMusicContextValue {
  tracks: FocusTrack[];
  currentTrack: FocusTrack;
  isPlaying: boolean;
  volume: number; // 0..1
  play: (trackId?: string) => Promise<void>;
  pause: () => void;
  stop: () => void;
  setTrack: (trackId: string) => Promise<void>;
  setVolume: (v: number) => void;
  toggle: () => Promise<void>;
}

const FocusMusicContext = createContext<FocusMusicContextValue | undefined>(undefined);

// All tracks now use generated audio - no external files needed
const TRACKS: FocusTrack[] = [
  // Best Picks
  { id: 'best', name: 'Best for Learning — Lo‑Fi Focus', description: 'Recommended', category: 'Best Picks', duration: 'Continuous', mood: 'calm', audioType: 'calm' },
  { id: 'ambient-study', name: 'Ambient Study Music', description: 'Deep focus soundscape', category: 'Best Picks', duration: 'Continuous', mood: 'focused', audioType: 'ambient' },
  
  // Piano & Classical
  { id: 'peaceful-piano', name: 'Peaceful Piano', description: 'Gentle piano for concentration', category: 'Piano', duration: 'Continuous', mood: 'peaceful', audioType: 'calm' },
  { id: 'classical-bach', name: 'Classical Focus', description: 'Baroque precision for deep work', category: 'Classical', duration: 'Continuous', mood: 'focused', audioType: 'calm' },
  
  // Ambient & Electronic
  { id: 'ambient-space', name: 'Ambient Space', description: 'Gentle ambient atmospheres', category: 'Ambient', duration: 'Continuous', mood: 'relaxed', audioType: 'ambient' },
  { id: 'lofi-beats', name: 'Lo‑Fi Hip Hop', description: 'Chill beats for studying', category: 'Electronic', duration: 'Continuous', mood: 'relaxed', audioType: 'calm' },
  
  // Nature Sounds
  { id: 'nature-rain', name: 'Gentle Rain', description: 'Steady rainfall soundscape', category: 'Nature', duration: 'Continuous', mood: 'calming', audioType: 'nature' },
  { id: 'ocean-waves', name: 'Ocean Waves', description: 'Rhythmic waves for focus', category: 'Nature', duration: 'Continuous', mood: 'calming', audioType: 'nature' },
  { id: 'nature-forest', name: 'Forest Ambience', description: 'Deep forest soundscape', category: 'Nature', duration: 'Continuous', mood: 'natural', audioType: 'nature' },
  
  // Specialized Focus
  { id: 'binaural-focus', name: 'Binaural Focus Beats', description: 'Concentration-enhancing beats', category: 'Focus Sounds', duration: 'Continuous', mood: 'focused', audioType: 'focus' },
  { id: 'brown-noise', name: 'Brown Noise Deep', description: 'Deep brown noise for focus', category: 'Focus Sounds', duration: 'Continuous', mood: 'grounding', audioType: 'nature' },
];

export const FocusMusicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentId, setCurrentId] = useState<string>('best');
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState<number>(0.6);

  const currentTrack = useMemo(() => TRACKS.find(t => t.id === currentId) || TRACKS[0], [currentId]);

  const ensureAudio = () => {
    if (!audioRef.current) {
      const el = document.createElement('audio');
      el.setAttribute('preload', 'auto');
      el.setAttribute('playsinline', '');
      el.loop = true;
      el.volume = volume;
      (el.style as any).display = 'none';
      document.body.appendChild(el);
      el.addEventListener('ended', () => setIsPlaying(false));
      el.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        setIsPlaying(false);
      });
      audioRef.current = el;
    }
    return audioRef.current as HTMLAudioElement;
  };

  const loadTrackAudio = (audio: HTMLAudioElement, track: FocusTrack) => {
    // Generate audio based on track type - longer duration for seamless looping
    const audioUrl = createDemoAudioBlob(track.audioType, 60);
    audio.src = audioUrl;
    try { 
      audio.load(); 
    } catch (error) { 
      console.error('Error loading audio:', error); 
    }
  };

  const play = useCallback(async (trackId?: string) => {
    const idToPlay = trackId ?? currentId;
    const track = TRACKS.find(t => t.id === idToPlay) || TRACKS[0];
    setCurrentId(track.id);

    const audio = ensureAudio();
    loadTrackAudio(audio, track);

    audio.play().then(() => {
      console.log('Audio started playing:', track.name);
      setIsPlaying(true);
    }).catch((error) => {
      console.error('Audio play failed:', error, 'for track:', track.name);
      setIsPlaying(false);
    });
  }, [currentId, volume]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      setIsPlaying(false);
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
    }
  }, []);

  const setTrack = useCallback(async (trackId: string) => {
    setCurrentId(trackId);
    const audio = ensureAudio();
    const track = TRACKS.find(t => t.id === trackId) || TRACKS[0];
    loadTrackAudio(audio, track);
  }, []);

  const setVolume = useCallback((v: number) => {
    const vol = Math.max(0, Math.min(1, v));
    setVolumeState(vol);
    if (audioRef.current) audioRef.current.volume = vol;
  }, []);

  const toggle = useCallback(async () => {
    const audio = ensureAudio();
    if (audio.paused) {
      loadTrackAudio(audio, currentTrack);
      audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, [currentTrack]);

  const value: FocusMusicContextValue = {
    tracks: TRACKS,
    currentTrack,
    isPlaying,
    volume,
    play,
    pause,
    stop,
    setTrack,
    setVolume,
    toggle,
  };

  return (
    <>
      <audio ref={audioRef} preload="auto" playsInline loop style={{ display: 'none' }} />
      <FocusMusicContext.Provider value={value}>{children}</FocusMusicContext.Provider>
    </>
  );
};

export const useFocusMusic = () => {
  const ctx = useContext(FocusMusicContext);
  if (!ctx) throw new Error('useFocusMusic must be used within FocusMusicProvider');
  return ctx;
};
